import { FunctionComponent, memo, useEffect, useState } from "react";
import "./AdminProfile.scss";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { FETCH_POLICY, FETCH_POLICY_NO_CACHE, messages, patterns, STUDENT_ROLE } from "../../utils/constants";
import images from "../../assets/images";
import InputField from "../../components/InputField/InputField";
import { useLazyQuery, useMutation } from "@apollo/client";
import ToasterWithMessage from "../../components/toaster";
import { UPDATE_ADMIN_PROFILE } from "./api/AdminProfileQuery";
import { visibility } from "../../redux/slices/rightSideAd";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import FileUpload from "../../components/Popups/FileUpload";
import { logout, setAdminProfile, setAdminProfileImage, setSplashvisible } from "../../redux/slices/authSlice";
import { schoolAdmin } from "../AdminClassroom/components/AdminClassroomForm";
import { getDecryptedDataWithSecretKey, getScreenDimensions } from "../../utils/helper";
import { GET_CURRENT_USER } from "../AdminDashboard/api/AdminDashboardQuery";
import Loader from "../../components/Loader/Loader";
import MobileHeader from "../../components/MobileHeader/MobileHeader";
import ButtonStyle from "../../components/ButtonStyle/ButtonStyle";
import { trackButtonClick } from "../../firebase/analyticsHelper";
import { ButtonClickEvents } from "../../firebase/constants/events";

export type AdminProfileType = {
  className?: string;
};

interface IFormInput {
  imageUrl?: string;
  schoolName: string;
  email: string;
  website: string;
  description?: string;
  username: string;
  password: string;
}

const schema = yup.object().shape({
  imageUrl: yup.string().trim(),
  schoolName: yup.string().required(messages.schoolName).max(100, 'Name should not exceed 100 characters'),
  email: yup.string().required(messages.email).matches(patterns.email, messages.validEmail),
  website: yup.string().required(messages.website).matches(patterns.websiteRegex, messages.invalidWebsite),
  description: yup.string().trim()
    .test(
      'description-length',
      'Description must be between 1 and 500 characters long',
      (value: string | undefined) =>  {
        return value === '' || value === undefined || (value.length >= 1 && value.length <= 500)
      }
    ),
  username: yup
    .string()
    .strict()
    .trim(messages.invalidUsername)
    .required(messages.username)
    .matches(patterns.username, messages.validUsername)
    .matches(patterns.usernameStartWithAlphabets, messages.usernameStartWithAlphabets)
    .matches(patterns.consecutiveDotsDashesUnderscores, messages.noConsecutiveDotsDashesUnderscores)
    .min(6, messages?.usernameMinLength)
    .max(50, messages?.usernameMaxLength)
    .test(
      "not-only-numbers",
      messages.invalidUsername,
      (value: string | undefined) => {
        if (value) {
          return !patterns.onlyNumbers.test(value);
        }
        return true;
      }
    ),
  password: yup
    .string()
    .strict()
    .trim(messages.invalidPassword)
    .required(messages.password)
    .matches(patterns?.password, messages?.validPassword)
    .min(8, messages.passwordMinLength)
    .max(30, messages.passwordMaxLength)
});

const AdminProfile: FunctionComponent<AdminProfileType> = memo(() => {
  const { schoolProfileImage, addIcon, backArrow } = images;
  const [isDisable, setIsDisable] = useState<boolean>(true);
  const [profileData, setProfileData] = useState<schoolAdmin>();
  const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
  const [url, setUrl] = useState<string>();
  const baseUrl: any = process.env.REACT_APP_FRONTEND_URL;
  const dispatch = useAppDispatch();
  const role = useAppSelector((state) => state.auth.role);
  const studentClasscode: string |null = localStorage.getItem('studentClassCode')

  useEffect(() => {
    dispatch(visibility({ isVisible: false }));
  }, [])

  const methods = useForm<IFormInput>({
    resolver: yupResolver(schema),
  });

  const { handleSubmit, reset } = methods;
  const { success, error } = ToasterWithMessage()

  // Update admin profile API
  const [updataAdmin] = useMutation(UPDATE_ADMIN_PROFILE, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (data) => {
      success("Profile updated successfully");
    },
    onError: (err) => {
      if(err?.message?.includes("Password has previously been used")){
        error(err?.message)
      }else{
        error("Profile update failed");
      }
    }
  });


  // Get admin profile API
  const [getCurrentUser, { loading }] = useLazyQuery(GET_CURRENT_USER, {
    fetchPolicy: FETCH_POLICY_NO_CACHE,
    onCompleted: (data) => {
      if (data && data?.getCurrentUser) {
        setProfileData(data?.getCurrentUser);
        dispatch(setAdminProfile({ profile: data }))
        const profile = data?.getCurrentUser;
        dispatch(setAdminProfileImage({ adminProfileImage: profile?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saAvatarUrl ? baseUrl + profile?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saAvatarUrl : schoolProfileImage }))
        setUrl(profile?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saAvatarUrl || "");
        const decodePassword = getDecryptedDataWithSecretKey(profile?.nodes?.[0]?.uUserPassword);

        reset({
          imageUrl: profile?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saAvatarUrl || "",
          schoolName: profile?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saName || "",
          email: profile?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saEmailId || "",
          website: profile?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saWebsite || "",
          description: profile?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saDescription || "",
          username: profile?.nodes?.[0]?.uUserName || "",
          password: decodePassword.originalText || ""
        });
      }
    },
    onError: () => {
      error("Something went wrong while fetching user profile!");
    }
  });

  useEffect(() => {
    if(role === STUDENT_ROLE){
        getCurrentUser({
          variables : {
            classCode : studentClasscode
          }
        })
      }
    else {
      getCurrentUser({
        variables : {
            classCode : ""
          }
      });
    }
  }, []);

  const checkValuesAreDifferent = (data: IFormInput) => {
    const decryptedPassword = getDecryptedDataWithSecretKey(profileData?.nodes?.[0]?.uUserPassword);
    const profileDataDescription = profileData?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saDescription;
    return (
      profileData?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saName !== data?.schoolName ||
      profileData?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saEmailId !== data?.email ||
      profileData?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saWebsite !== data?.website ||
      ((profileDataDescription != null) ? (profileDataDescription !== data?.description) : (Boolean(profileDataDescription) !== Boolean(data?.description))) ||
      profileData?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saAvatarUrl !== url ||
      profileData?.nodes?.[0]?.uUserName !== data?.username ||
      decryptedPassword.originalText !== data?.password
    )
  };

  // Update admin profile function
  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    trackButtonClick(ButtonClickEvents?.SCHOOL_PROFILE_SAVE, {
        description : "User clicked school profile save button",
        page: 'AdminProfile'
    });

    let input: any = null;
    const decryptedPassword = getDecryptedDataWithSecretKey(profileData?.nodes?.[0]?.uUserPassword);
    if (checkValuesAreDifferent(data)) {
      input = {
        schoolName: data?.schoolName.trim(),
        emailId: data?.email.trim(),
        website: data?.website.trim(),
        description: data?.description === '' ? null : data?.description?.trim(),
        schoolAvatarFileName: url,
        userName: profileData?.nodes?.[0]?.uUserName !== data?.username ? data?.username.trim() : null,
        passWord: decryptedPassword.originalText !== data?.password ? data?.password.trim() : null
      }

      if (Object.keys(input).length > 0) {
        updataAdmin({
          variables: {
            input: input
          }
        }).then((result) => {
          if ((profileData?.nodes?.[0]?.uUserName !== data?.username || decryptedPassword.originalText !== data?.password) && !result?.errors) {
            setTimeout(() => {
              dispatch(logout());
              dispatch(setSplashvisible());
            },500);
          } else {
            getCurrentUser({
              variables : {
                  classCode : ""
                }
            });
          }
        })
      }
    }
    setIsDisable(true);
  };

  const handleUploadPopup = () => {
    if (isDisable === false) {
      setIsUploadOpen(true);
    }
  }

  return (
    <div className="mainSection">
      {(loading || profileData === undefined) ? <Loader /> :
       (<FormProvider {...methods}>
          <form autoComplete="off" className="boxInfo" onSubmit={handleSubmit(onSubmit)} >
          {getScreenDimensions()?.isMobileScreen &&
            <div 
                className="adminMobileHeaderContainer" 
                style={{justifyContent: isDisable ? "space-between":"flex-start"}} 
                onClick={() => {
                  if(!isDisable) {
                    setIsDisable(true)
                  }
                }}
            >
              {!isDisable && <div className="backArrow">
                    <img src={backArrow} className="backIcon" alt="backArrowIcon"/>
                </div>}
              <div className="adminProfileTitleTextStyle">{isDisable ? "School": "Edit"}</div>
              {isDisable && <MobileHeader />}
            </div>}
            {!getScreenDimensions()?.isMobileScreen && <div className="ctaSet">
              {!isDisable ?
                <>
                  <div></div>
                  <button className="saveCta" type="submit">
                    <label className="save">Save</label>
                  </button>
                </> :
                <>
                  <div></div>
                  <div className="saveCta" onClick={() => setIsDisable(false)}>
                    <label className="save">Edit</label>
                  </div>
                </>}
            </div> }
            <div className="formContainer">
                <div className="profilePlaceholder">
                  <div className="profileSet">
                    <img className="logoSidebarIcon" alt="logoSidebarIcon" src={url ? baseUrl + url : schoolProfileImage} />
                    <div className="ctaUpload">
                      <label className="addProfileLogo">{getScreenDimensions()?.isMobileScreen ? "Upload Logo" : "Add Profile Logo"}</label>
                      <div className="solidinterfaceplus" onClick={handleUploadPopup}>
                        <div className="solidinterfaceplusChild" />
                        <img className="unionIcon" alt="addIcon" src={addIcon} />
                      </div>
                    </div>
                    {
                      isUploadOpen ?
                        <FileUpload page="Admin Profile" type='profile' setUrl={setUrl} open={isUploadOpen} setOpen={setIsUploadOpen} folderName="images" />
                        : null
                    }
                  </div>
                  <div className="details">
                    <div className="fieldSet">
                      <div className="fieldName">
                        <div className="label">
                          <label className="nameOfTeacher">Name of School</label>
                        </div>
                        <InputField type="text" classname="subjectStyle" name="schoolName" label="Name" size="full" isDisable={isDisable} />
                      </div>
                      <div className="fieldName">
                        <label className="label1">
                          <label className="nameOfTeacher">Email</label>
                        </label>
                        <InputField type="text" name="email" label="Email" size="full" isDisable={isDisable} />
                      </div>
                      <div className="fieldName">
                        <div className="label">
                          <label className="nameOfTeacher">Website</label>
                        </div>
                        <InputField type="text" name="website" label="" size="full" isDisable={isDisable} />
                      </div>
                      <div className="fieldName">
                        <label className="label1">
                          <label className="nameOfTeacher">Description</label>
                        </label>
                        <InputField type="textarea" name="description" label="" size="full" classname="field3" isDisable={isDisable} />
                      </div>
                      <div className="fieldName userDetails">
                        <div className="label">
                          <label className="nameOfTeacher">Login Access</label>
                        </div>
                        <div className="fieldTwo">
                          <div className="field5">
                            <InputField type="text" name="username" label="Username" size="full" isDisable={isDisable} />
                            <InputField type="password" name="password" label="Password" size="full" isDisable={isDisable} />
                          </div>
                        </div>
                        {getScreenDimensions()?.isMobileScreen &&<div className="adminSaveButtonMobile" onClick={() => setIsDisable(false)}>
                          <ButtonStyle paddingLeft='26px' paddingRight='28px' width='100%' height='49px' text= { isDisable ? "Edit" : "Done"} type="submit"/>
                        </div>}
                      </div>
                    </div>
                  </div>
                </div>
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  );
});

export default AdminProfile;
